import { isNoOpDrop, isSameParent } from './dropPosition';
import { computeGroupBlockMove } from './groupBlockMove';
import {
  type DropNode,
  type DropNodeId,
  isLevelCompatible,
  validateParentReparent,
} from './rowReorderValidation';

export interface LeveledDropNode extends DropNode {
  levelOrder: number | undefined;
}

export interface DragSession {
  movableParentIds: (DropNodeId | null)[];
  prevSiblingId: DropNodeId | null;
  nextSiblingId: DropNodeId | null;
  selectionBlocked: boolean;
  selectionSpansParents: boolean;
  allLocked: boolean;
  groupReorder?: { siblingIds: DropNodeId[]; selectedIds: DropNodeId[] } | null;
}

export type DropResolution =
  | { kind: 'reparent' }
  | { kind: 'sibling'; position: 'above' | 'below' }
  | { kind: 'crossParent'; position: 'above' | 'below' }
  | { kind: 'rejected'; blockedByLock: boolean }
  | { kind: 'none' };

interface ResolveDropTargetInput {
  source: LeveledDropNode;
  target: LeveledDropNode;
  pointerPosition: 'above' | 'below';
  session: DragSession;
  siblingsOnly: boolean;
  canReparent: boolean;
}

export const resolveDropTarget = ({
  source,
  target,
  pointerPosition,
  session,
  siblingsOnly,
  canReparent,
}: ResolveDropTargetInput): DropResolution => {
  if (session.selectionBlocked) return { kind: 'rejected', blockedByLock: false };

  const wouldReparent =
    canReparent &&
    validateParentReparent({
      sourceNode: source,
      targetNode: target,
      sourceTypeLevelOrder: source.levelOrder,
      targetTypeLevelOrder: target.levelOrder,
      selectionParentIds: session.movableParentIds,
    });

  if (wouldReparent) {
    return session.allLocked ? { kind: 'rejected', blockedByLock: true } : { kind: 'reparent' };
  }

  const moveSelectionAsCrossParent = session.selectionSpansParents && canReparent;

  if (isSameParent(source, target) && !moveSelectionAsCrossParent) {
    const { groupReorder } = session;
    const isNoOp = groupReorder
      ? computeGroupBlockMove(groupReorder.siblingIds, groupReorder.selectedIds, {
          rowId: target.id,
          position: pointerPosition,
        }) === null
      : isNoOpDrop(
          source.id,
          target.id,
          pointerPosition,
          session.prevSiblingId,
          session.nextSiblingId,
        );
    if (isNoOp) return { kind: 'none' };
    return { kind: 'sibling', position: pointerPosition };
  }

  if (siblingsOnly) return { kind: 'rejected', blockedByLock: false };
  if (target.id === source.id) return { kind: 'none' };
  if (session.allLocked) return { kind: 'rejected', blockedByLock: true };
  if (!isLevelCompatible(source.levelOrder, target.levelOrder, source.depth, target.depth)) {
    return { kind: 'rejected', blockedByLock: false };
  }
  return { kind: 'crossParent', position: pointerPosition };
};

export type ActionableDropResolution = Extract<
  DropResolution,
  { kind: 'reparent' | 'sibling' | 'crossParent' }
>;

export const isActionableDropResolution = (
  resolution: DropResolution,
): resolution is ActionableDropResolution =>
  resolution.kind === 'reparent' ||
  resolution.kind === 'sibling' ||
  resolution.kind === 'crossParent';
